import { FC, useEffect, useState } from 'react';
import { Image, Button } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { PickupStatsCardProps } from './PickupStatsCard.types';
import { StyledPickupStatsCard, StyledProgressFill } from './PickupStatsCard.styles';
import { PropertyImage, PropertyImageType } from '../../../organisms/SearchResults/PropertyCard/PropertyCard.types';
import { getFormattedDateObject, getFormattedDates, getPropertyImages } from '../../../utils';

export const PickupStatsCard: FC<PickupStatsCardProps> = ({
  cardLabels,
  cardData,
  onClick,
  disableAlerts = false,
  showAlertCta = true,
  isNewlyAdded = false,
}) => {
  const { alert, alerts, guestRooms, continueMessage, ctas } = cardLabels;
  const { media, propertyName, alertCount, date, rooms } = cardData;

  const [image, setImage] = useState<PropertyImage>();

  useEffect(() => {
    setImage(getPropertyImages(PropertyImageType.REGULAR_DESKTOP, media?.primaryImage)[0]);
  }, [media]);

  const alertLabel = alertCount === 1 ? alert : alerts;
  const hasAlerts = alertCount > 0;
  const ctaLabel = disableAlerts ? ctas.viewPickupStats : hasAlerts ? ctas.editAlerts : ctas.setAlerts;
  const ctaAriaLabel = disableAlerts
    ? ctas.viewPickupStatsAriaLabel
    : hasAlerts
    ? ctas.editAlertAriaLabel
    : ctas.setAlertAriaLabel;

  const ctaTrackLocation = hasAlerts ? ctas.editAlertsTrackLocation : ctas.setAlertsTrackLocation;
  const ctaTrackDescription = hasAlerts ? ctas.editAlertsTrackDescription : ctas.setAlertsTrackDescription;
  const ctaTrackValue = !disableAlerts ? `${ctaTrackLocation}|${ctaTrackDescription}|internal` : undefined;

  const pickupStatsPercent = rooms.totalContracted > 0 ? (rooms.totalPickups / rooms.totalContracted) * 100 : 0;

  const formattedDates = date
    ? getFormattedDates(
        getFormattedDateObject(date.startDate),
        getFormattedDateObject(date.endDate),
        'dateWithDayAndMonth'
      )
    : '';

  return (
    <StyledPickupStatsCard data-testid="groups-PickupStatsCard" data-component-name="m-groups-PickupStatsCard">
      <div className="image-section">
        <Image
          defaultImageURL={image?.defaultImageUrl}
          altText={image?.altText}
          customClass="property-image"
          loading="eager"
        />
        {!disableAlerts && alertCount > 0 && (
          <div className="m-badge-inline-neutral alert-badge">
            <span className="icon-bell"></span> {alertCount} {alertLabel}
          </div>
        )}
      </div>
      <div className="card-body p-3">
        <h2 className="t-subtitle-m m-ellipsis-1line mb-2">{propertyName}</h2>
        <div className="card-details d-flex align-items-center">
          <div className="dates t-font-xs">
            <span className="icon icon-calendar t-font-s mr-1"></span>
            {formattedDates}
          </div>
          <span className="separator px-2">|</span>
          <div className="rooms t-font-xs">
            <span className="icon icon-room-ready t-font-s mr-1"></span>
            {rooms.totalContracted} {guestRooms}
          </div>
        </div>
        <div className="pickup-progress mt-3">
          <div className="pickup-progress-track">
            <StyledProgressFill $fillWidth={pickupStatsPercent} />
          </div>
          <span className="pickup-progress-count t-font-xs">
            {rooms.totalPickups}/{rooms.totalContracted}
          </span>
        </div>
      </div>
      {showAlertCta ? (
        <div className="card-footer d-flex justify-content-end">
          <Button
            className={clsx('footer-cta', ctaTrackValue && 'custom_click_track')}
            ariaLabel={ctaAriaLabel}
            callback={onClick}
            custom_click_track_value={ctaTrackValue}
          >
            {ctaLabel}
            <span className="icon icon-forward-arrow px-1"></span>
          </Button>
        </div>
      ) : isNewlyAdded && continueMessage ? (
        <div className="card-footer">
          <p className="t-font-alt-s mb-0">{continueMessage}</p>
        </div>
      ) : null}
    </StyledPickupStatsCard>
  );
};
